import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { updateModuleProgress } from '../../../SkillsApi/SkillsApi'
import './AiModuleTwo.css'

const MODULE_ID = 2
const SECTION = 'AiSection'

// TODO: swap for the actual AI Module 2 video URL
const VIDEO_URL = 'https://upskld-demo-videos.s3.ap-south-1.amazonaws.com/AI_Course_Module_2.mp4'

// TODO: swap these for questions that match AI Module 2's actual video content —
// these are placeholders on general AI/ML topics, same Tanglish style as Module 1.
const ASSESSMENT_QUESTIONS = [
  {
    id: 'q1',
    question: 'Convolutional Neural Networks (CNN) பொதுவா எந்த task-க்கு use பண்றாங்க?',
    options: [
      { key: 'a', label: 'Image recognition and processing' },
      { key: 'b', label: 'Text-only translation' },
      { key: 'c', label: 'Audio compression மட்டும்' },
      { key: 'd', label: 'Database indexing' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q2',
    question: '"Gradient descent" algorithm-ல முக்கிய நோக்கம் என்ன?',
    options: [
      { key: 'a', label: 'Minimizing the loss function' },
      { key: 'b', label: 'Increasing dataset size' },
      { key: 'c', label: 'Compressing the model file' },
      { key: 'd', label: 'Encrypting the training data' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q3',
    question: '"Transfer learning"-ன் முக்கிய பயன் என்ன?',
    options: [
      { key: 'a', label: 'Reusing a pretrained model for a new related task' },
      { key: 'b', label: 'Transferring files between servers' },
      { key: 'c', label: 'Copying data between databases' },
      { key: 'd', label: 'Backing up model weights only' },
    ],
    correctKey: 'a',
  },
]

const AiModuleTwo = () => {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [watchPercent, setWatchPercent] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const lastSentMilestoneRef = useRef(0)

  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Same right-click / DevTools-shortcut deterrent as AI Module 1.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.warn('Right-click is disabled on this page.')
    }

    const handleKeyDown = (e) => {
      const isDevToolsShortcut =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u'].includes(e.key))

      if (isDevToolsShortcut) {
        e.preventDefault()
        toast.warn('Inspect is disabled on this page.')
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const sendMilestone = async (milestone) => {
    if (milestone <= lastSentMilestoneRef.current) return
    lastSentMilestoneRef.current = milestone
    await updateModuleProgress(SECTION, MODULE_ID, milestone)
  }

  // Milestones capped at 90 during playback — the final 100% is only sent
  // once the assessment is finished too (see handleFinish), so AI Module 3
  // only unlocks once BOTH the video and the quiz are done.
  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target
    if (!duration) return

    const percent = Math.min(100, Math.floor((currentTime / duration) * 100))
    setWatchPercent((prev) => (percent > prev ? percent : prev))

    const crossedMilestone = Math.min(90, Math.floor(percent / 10) * 10)
    if (crossedMilestone > lastSentMilestoneRef.current) {
      sendMilestone(crossedMilestone)
    }
  }

  const handleVideoEnded = () => {
    setWatchPercent(100)
    setVideoEnded(true)
    toast.info('Video watched! Now complete the assessment below.')
  }

  const handleAnswerSelect = (questionId, optionKey, correctKey) => {
    if (answers[questionId]) return

    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }))

    if (optionKey === correctKey) {
      toast.success('Correct answer!')
    } else {
      toast.error('Wrong answer!')
    }
  }

  const allQuestionsAnswered = ASSESSMENT_QUESTIONS.every((q) => answers[q.id])
  const canFinish = videoEnded && allQuestionsAnswered

  const handleFinish = async () => {
    if (!canFinish || finishing) return
    setFinishing(true)
    await sendMilestone(100)
    toast.success('Module 2 complete — Module 3 unlocked!')
    navigate('/ai_course')
  }

  return (
    <div className="AiModuleTwo_wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="AiModuleTwo_content">
        <h2 className="AiModuleTwo_title">Module 2: Deep Dive</h2>
        <p className="AiModuleTwo_subtitle">
          Watch the full video, then complete the short assessment to unlock Module 3.
        </p>

        <div className="AiModuleTwo_video_wrap">
          <video
            className="AiModuleTwo_video"
            src={VIDEO_URL}
            controls
            controlsList="nodownload"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        </div>

        <div className="AiModuleTwo_progress_row">
          <div className="AiModuleTwo_progress_track">
            <div
              className="AiModuleTwo_progress_fill"
              style={{ width: `${watchPercent}%` }}
            />
          </div>
          <span className="AiModuleTwo_progress_label">{watchPercent}% watched</span>
        </div>

        <div className="AiModuleTwo_assessment">
          <h3 className="AiModuleTwo_assessment_title">Module Assessment</h3>
          <p className="AiModuleTwo_assessment_sub">
            {videoEnded
              ? `Answer all ${ASSESSMENT_QUESTIONS.length} questions to complete this module.`
              : 'Finish watching the video above to unlock the assessment.'}
          </p>

          {ASSESSMENT_QUESTIONS.map((q, qIndex) => {
            const selected = answers[q.id]

            return (
              <div className="AiModuleTwo_question_card" key={q.id}>
                <p className="AiModuleTwo_question_text">
                  <span className="AiModuleTwo_question_num">Q{qIndex + 1}.</span> {q.question}
                </p>

                <div className="AiModuleTwo_options_grid">
                  {q.options.map((opt) => {
                    const isSelected = selected === opt.key
                    const isCorrectOpt = opt.key === q.correctKey
                    let stateClass = ''

                    if (selected) {
                      if (isSelected && isCorrectOpt) stateClass = 'AiModuleTwo_option_correct'
                      else if (isSelected && !isCorrectOpt) stateClass = 'AiModuleTwo_option_wrong'
                      else if (!isSelected && isCorrectOpt) stateClass = 'AiModuleTwo_option_reveal'
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={`AiModuleTwo_option_btn ${stateClass}`}
                        onClick={() => handleAnswerSelect(q.id, opt.key, q.correctKey)}
                        disabled={!videoEnded || !!selected}
                      >
                        <span className="AiModuleTwo_option_key">{opt.key.toUpperCase()}</span>
                        <span className="AiModuleTwo_option_label">{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="AiModuleTwo_complete_btn"
            onClick={handleFinish}
            disabled={!canFinish || finishing}
          >
            {finishing ? 'Saving...' : (
              <>
                Complete Module <span className="AiModuleTwo_complete_check">&#10003;</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AiModuleTwo